import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  AuditOutlined,
  CheckSquareOutlined,
  CloseCircleOutlined,
  CloseSquareOutlined,
  DisconnectOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';

import DeindexationStatusEnum from 'redpoints-model-js/enumeration/DeindexationStatusEnum';
import EnforcementStatusEnum from 'redpoints-model-js/enumeration/EnforcementStatusEnum';
import LinkTypeEnum from 'redpoints-model-js/enumeration/LinkTypeEnum';
import LinkSetStatusObject from 'redpoints-model-js/model/system/LinkSetStatusObject';
import LinkSetUnavailableObject from 'redpoints-model-js/model/system/LinkSetUnavailableObject';

import { isUrl } from 'redpoints-front-components/helpers';
import { Col, Row } from 'redpoints-front-components/index';

import {
  linksManagementLinkAvailabilityUrlFormatError,
  linksManagementLinksSetContentLinksUrls,
  linksManagementLinksSetDetectionLinksUrls,
  linksManagementLinksSetProcessingContentLinks,
  linksManagementLinksSetProcessingDetectionLinks,
  linksManagementSetDeindexationStatusByUrl,
  linksManagementSetLinkAvailability,
  linksManagementSetStatusLinkByUrl,
  linksManagementUrlFormatError,
} from '../../../actions/linksManagementActions';
import {
  getAreContentLinksProcessed,
  getAreDetectionLinksProcessed,
  getLinksManagementEnforcementSelectedReason,
  getLinksManagementLinksContentLinkUrlsIntroduced,
  getLinksManagementLinksDetectionLinkUrlsIntroduced,
  getLinksManagementLinksProcessingContentLinks,
  getLinksManagementLinksProcessingDetectionLinks,
  getLinksManagementLinksProcessingStatus,
} from '../../../reducers/tasks/linksManagement';
import LinksManagementAvailabilityModal from './LinksManagementAvailabilityModal';
import LinksManagementCloseModal from './LinksManagementCloseModal';
import LinksManagementComplaintWithDrawnReasonsModal from './LinksManagementComplaintWithDrawnReasonsModal';
import LinksManagementEnforcementDisruptedModal from './LinksManagementEnforcementDisruptedModal';
import LinksManagementRelaunchEnforcementAction from './LinksManagementRelaunchEnforcementAction';
import LinksManagementSearchEngineModal from './LinksManagementSearchEngineModal';
import LinksManagementViewerActionsButton from './LinksManagementViewerActionsButton';
import LinksManagementSetLinkStatusEnum from '../../../enumerations/LinksManagementSetLinkStatusEnum';
import { translate } from '../../../helpers/languageHelper';
import { transformUrlsToLinkObjects } from './linksManagementHelpers';

import './linksManagement.less';

const LINK_AVAILABILITY_ACTIONS = Object.freeze({
  AVAILABLE: 'available',
  UNAVAILABLE: 'unavailable',
});

const LinksManagementViewerActions = ({
  areContentLinksProcessed,
  areContentLinksProcessing,
  areDetectionLinksProcessed,
  areDetectionLinksProcessing,
  contentLinkUrls,
  contentLinks,
  detectionLinkUrls,
  detectionLinks,
  form,
  linksManagementLinkAvailabilityUrlFormatError,
  linksManagementLinksSetContentLinksUrls,
  linksManagementLinksSetDetectionLinksUrls,
  linksManagementLinksSetProcessingContentLinks,
  linksManagementLinksSetProcessingDetectionLinks,
  linksManagementSetDeindexationStatusByUrl,
  linksManagementSetLinkAvailability,
  linksManagementSetStatusLinkByUrl,
  linksManagementUrlFormatError,
  processingStatus,
  selectedReason,
}) => {
  const { setFieldsValue, validateFields } = form;
  const [linkAvailabilityAction, setLinkAvailabilityAction] = useState();

  const setStatusLinks = useCallback(
    (links, type) => {
      const link = links.find(
        link => link.status === LinksManagementSetLinkStatusEnum.WAITING
      );
      if (link) {
        const linkSetStatusObject = new LinkSetStatusObject();
        linkSetStatusObject.url = link.url;
        linkSetStatusObject.linkType = type;

        const linkSetUnavailableObject = new LinkSetUnavailableObject();
        linkSetUnavailableObject.url = link.url;
        linkSetUnavailableObject.linkType = type;

        const hasReasonEnforcementDisrupted =
          selectedReason &&
          processingStatus === EnforcementStatusEnum.ENFORCEMENT_DISRUPTED;

        const hasReasonComplaintWithdrawn =
          selectedReason &&
          processingStatus === EnforcementStatusEnum.COMPLAINT_WITHDRAWN;

        const hasSearchEngineDeindexed =
          selectedReason &&
          processingStatus === EnforcementStatusEnum.DEINDEXED;

        const isAvailableAction =
          linkAvailabilityAction === LINK_AVAILABILITY_ACTIONS.AVAILABLE;

        const isUnavailableAction =
          linkAvailabilityAction === LINK_AVAILABILITY_ACTIONS.UNAVAILABLE;

        const isAvailabilityAction = isAvailableAction || isUnavailableAction;

        if (hasReasonEnforcementDisrupted) {
          linkSetStatusObject.enforcementDisruptedReason = selectedReason;
        }

        if (hasReasonComplaintWithdrawn) {
          linkSetStatusObject.enforcementComplaintWithdrawnReason = selectedReason;
        }

        if (hasSearchEngineDeindexed) {
          linkSetStatusObject.searchEngineDomainId =
            selectedReason.searchEngineDomainId;
          linkSetStatusObject.deindexationStatus =
            DeindexationStatusEnum.REQUEST_APPROVED;
        }

        const isNotValidUrlForSetStatusAction =
          !isUrl(link.url) && !isAvailabilityAction;

        const isNotValidUrlForAvailabilityAction =
          !isUrl(link.url) && isAvailabilityAction;

        if (isNotValidUrlForSetStatusAction) {
          linksManagementUrlFormatError({ linkSetStatusObject });
        } else if (isNotValidUrlForAvailabilityAction) {
          linksManagementLinkAvailabilityUrlFormatError({
            linkSetUnavailableObject,
          });
        } else if (hasSearchEngineDeindexed) {
          linksManagementSetDeindexationStatusByUrl(linkSetStatusObject);
        } else if (isAvailabilityAction) {
          linksManagementSetLinkAvailability({
            linkType: type,
            linkUrl: link.url,
            unavailable: isUnavailableAction,
          });
        } else {
          linkSetStatusObject.enforcementStatus = processingStatus;
          linksManagementSetStatusLinkByUrl({ linkSetStatusObject });
        }
      }
    },
    [
      linkAvailabilityAction,
      linksManagementLinkAvailabilityUrlFormatError,
      linksManagementSetDeindexationStatusByUrl,
      linksManagementSetLinkAvailability,
      linksManagementSetStatusLinkByUrl,
      linksManagementUrlFormatError,
      processingStatus,
      selectedReason,
    ]
  );

  useEffect(() => {
    if (detectionLinks.length > 0) {
      setStatusLinks(detectionLinks, LinkTypeEnum.DETECTION);
    }
    if (contentLinks.length > 0) {
      setStatusLinks(contentLinks, LinkTypeEnum.CONTENT);
    }
  }, [detectionLinks, contentLinks, setStatusLinks]);

  const getSuccessLinksUrls = links => {
    return links
      .filter(
        ({ status }) => status !== LinksManagementSetLinkStatusEnum.SUCCESS
      )
      .map(link => `${link.url}\n`)
      .join('')
      .toString();
  };

  useEffect(() => {
    if (detectionLinks.length > 0 && areDetectionLinksProcessed) {
      const notSuccessLinksUrls = getSuccessLinksUrls(detectionLinks);
      linksManagementLinksSetDetectionLinksUrls({
        detectionLinkUrls: notSuccessLinksUrls,
      });
      linksManagementLinksSetProcessingDetectionLinks({
        detectionLinks: [],
        newStatus: null,
        selectedReason: null,
      });
      setFieldsValue({
        [LinkTypeEnum.DETECTION.name]: notSuccessLinksUrls,
      });
      setLinkAvailabilityAction();
    }
  }, [
    areDetectionLinksProcessed,
    detectionLinks,
    linksManagementLinksSetDetectionLinksUrls,
    linksManagementLinksSetProcessingDetectionLinks,
    setFieldsValue,
  ]);

  useEffect(() => {
    if (contentLinks.length > 0 && areContentLinksProcessed) {
      const notSuccessLinksUrls = getSuccessLinksUrls(contentLinks);
      linksManagementLinksSetContentLinksUrls({
        contentLinkUrls: notSuccessLinksUrls,
      });
      linksManagementLinksSetProcessingContentLinks({
        contentLinks: [],
        newStatus: null,
        selectedReason: null,
      });
      setFieldsValue({
        [LinkTypeEnum.CONTENT.name]: notSuccessLinksUrls,
      });
      setLinkAvailabilityAction();
    }
  }, [
    areContentLinksProcessed,
    contentLinks,
    linksManagementLinksSetContentLinksUrls,
    linksManagementLinksSetProcessingContentLinks,
    setFieldsValue,
  ]);

  const setLinks = useCallback(
    (linkUrls, type, newStatus, selectedReason) => {
      if (linkUrls && type === LinkTypeEnum.DETECTION) {
        linksManagementLinksSetProcessingDetectionLinks({
          detectionLinks: transformUrlsToLinkObjects(linkUrls),
          newStatus,
          selectedReason,
        });
      }
      if (linkUrls && type === LinkTypeEnum.CONTENT) {
        linksManagementLinksSetProcessingContentLinks({
          contentLinks: transformUrlsToLinkObjects(linkUrls),
          newStatus,
          selectedReason,
        });
      }
    },
    [
      linksManagementLinksSetProcessingDetectionLinks,
      linksManagementLinksSetProcessingContentLinks,
    ]
  );

  const [
    showEnforcementDisruptedModal,
    setShowEnforcementDisruptedModal,
  ] = useState(false);

  const [showCloseModal, setShowCloseModal] = useState(false);

  const handleClickCloseModal = useCallback(() => {
    validateFields()
      .then(() => {
        setShowCloseModal(true);
      })
      .catch(error => console.error(error)); // eslint-disable-line no-console
  }, [validateFields]);

  const handleCloseCloseModal = useCallback(() => setShowCloseModal(false), [
    setShowCloseModal,
  ]);

  const handleClickEnforcementDisruptedModal = useCallback(() => {
    validateFields()
      .then(() => {
        setShowEnforcementDisruptedModal(true);
      })
      .catch(error => console.error(error)); // eslint-disable-line no-console
  }, [validateFields]);

  const handleHideEnforcementDisruptedModal = useCallback(() => {
    setShowEnforcementDisruptedModal(false);
  }, [setShowEnforcementDisruptedModal]);

  const [
    showComplaintWithdrawnReasonsModal,
    setShowWithdrawnReasonsModal,
  ] = useState(false);

  const handelClickWithdrawnReasonsModal = useCallback(() => {
    validateFields()
      .then(() => {
        setShowWithdrawnReasonsModal(true);
      })
      .catch(error => console.error(error)); // eslint-disable-line no-console
  }, [validateFields]);

  const handleHideWithdrawnReasonsModal = useCallback(() => {
    setShowWithdrawnReasonsModal(false);
  }, [setShowWithdrawnReasonsModal]);

  const [showSearchEngineModal, setShowSearchEngineModal] = useState(false);

  const handelClickSearchEngineModal = useCallback(() => {
    validateFields()
      .then(() => {
        setShowSearchEngineModal(true);
      })
      .catch(error => console.error(error)); // eslint-disable-line no-console
  }, [validateFields]);

  const handleHideSearchEngineModal = useCallback(() => {
    setShowSearchEngineModal(false);
  }, [setShowSearchEngineModal]);

  const [showLinkAvailableModal, setShowLinkAvailableModal] = useState();

  const handleClickLinkAvailableModal = useCallback(() => {
    validateFields()
      .then(() => {
        setShowLinkAvailableModal(true);
      })
      .catch(error => console.error(error)); // eslint-disable-line no-console
  }, [validateFields]);

  const handleCloseLinkAvailableModal = () => {
    setShowLinkAvailableModal(false);
  };

  const [showLinkUnavailableModal, setShowLinkUnavailableModal] = useState();

  const handleClickLinkUnavailableModal = useCallback(() => {
    validateFields()
      .then(() => {
        setShowLinkUnavailableModal(true);
      })
      .catch(error => console.error(error)); // eslint-disable-line no-console
  }, [validateFields]);

  const handleCloseLinkUnavailableModal = () => {
    setShowLinkUnavailableModal(false);
  };

  const handleSetLinks = useCallback(
    newStatus => selectedReason => () => {
      setShowCloseModal(false);
      setShowEnforcementDisruptedModal(false);
      setShowLinkAvailableModal(false);
      setShowLinkUnavailableModal(false);
      setShowSearchEngineModal(false);
      setShowWithdrawnReasonsModal(false);

      validateFields()
        .then(() => {
          setLinks(
            detectionLinkUrls,
            LinkTypeEnum.DETECTION,
            newStatus,
            selectedReason
          );
          setLinks(
            contentLinkUrls,
            LinkTypeEnum.CONTENT,
            newStatus,
            selectedReason
          );
        })
        .catch(error => console.error(error)); // eslint-disable-line no-console
    },
    [
      contentLinkUrls,
      detectionLinkUrls,
      setLinks,
      setShowEnforcementDisruptedModal,
      setShowSearchEngineModal,
      setShowWithdrawnReasonsModal,
      validateFields,
    ]
  );

  const textAreasAreEmpty = !contentLinkUrls && !detectionLinkUrls;
  const linksAreProcessing =
    areContentLinksProcessing || areDetectionLinksProcessing;

  const disabled =
    textAreasAreEmpty || linksAreProcessing || form.isFieldValidating();

  return (
    <>
      <Row gutter={16} type="flex" justify="start">
        <Col>
          <LinksManagementViewerActionsButton
            disabled={disabled}
            onClick={handleClickCloseModal}
            title={translate('tasks.links_management.actions.close')}
          >
            <CloseCircleOutlined disabled={disabled} />
          </LinksManagementViewerActionsButton>
        </Col>
        <Col>
          <LinksManagementViewerActionsButton
            aria-label="file-excel"
            disabled={disabled}
            onClick={handleClickEnforcementDisruptedModal}
            title={translate('tasks.links_management.actions.enforcement')}
          >
            <FileExcelOutlined disabled={disabled} />
          </LinksManagementViewerActionsButton>
        </Col>
        <Col>
          <LinksManagementViewerActionsButton
            disabled={disabled}
            onClick={handelClickWithdrawnReasonsModal}
            title={translate('tasks.links_management.actions.complaint')}
          >
            <AuditOutlined disabled={disabled} />
          </LinksManagementViewerActionsButton>
        </Col>
        <Col>
          <LinksManagementViewerActionsButton
            disabled={disabled}
            onClick={handelClickSearchEngineModal}
            title={translate('tasks.links_management.actions.deindexed')}
          >
            <DisconnectOutlined disabled={disabled} />
          </LinksManagementViewerActionsButton>
        </Col>
        <Col>
          <LinksManagementViewerActionsButton
            disabled={disabled}
            onClick={handleClickLinkUnavailableModal}
            title={translate('tasks.links_management.actions.unavailable')}
          >
            <CloseSquareOutlined disabled={disabled} />
          </LinksManagementViewerActionsButton>
        </Col>
        <Col>
          <LinksManagementViewerActionsButton
            disabled={disabled}
            onClick={handleClickLinkAvailableModal}
            title={translate('tasks.links_management.actions.available')}
          >
            <CheckSquareOutlined disabled={disabled} />
          </LinksManagementViewerActionsButton>
        </Col>
        <Col>
          <LinksManagementRelaunchEnforcementAction
            disabled={disabled}
            validateFields={validateFields}
          />
        </Col>
      </Row>
      <LinksManagementCloseModal
        onAccept={handleSetLinks(EnforcementStatusEnum.CLOSED)()}
        onCancel={handleCloseCloseModal}
        visible={showCloseModal}
      />
      {showEnforcementDisruptedModal && (
        <LinksManagementEnforcementDisruptedModal
          onAccept={handleSetLinks(EnforcementStatusEnum.ENFORCEMENT_DISRUPTED)}
          onCancel={handleHideEnforcementDisruptedModal}
        />
      )}
      {showComplaintWithdrawnReasonsModal && (
        <LinksManagementComplaintWithDrawnReasonsModal
          onAccept={handleSetLinks(EnforcementStatusEnum.COMPLAINT_WITHDRAWN)}
          onCancel={handleHideWithdrawnReasonsModal}
        />
      )}
      {showSearchEngineModal && (
        <LinksManagementSearchEngineModal
          onAccept={handleSetLinks(EnforcementStatusEnum.DEINDEXED)}
          onCancel={handleHideSearchEngineModal}
        />
      )}
      {showLinkUnavailableModal && (
        <LinksManagementAvailabilityModal
          okText={translate('actions.confirm')}
          onAccept={handleSetLinks()}
          onCancel={handleCloseLinkUnavailableModal}
          onSetLinkAvailability={() =>
            setLinkAvailabilityAction(LINK_AVAILABILITY_ACTIONS.UNAVAILABLE)
          }
        >
          {translate('tasks.links_management.modal.linkUnAvailableModalLabel')}
        </LinksManagementAvailabilityModal>
      )}
      {showLinkAvailableModal && (
        <LinksManagementAvailabilityModal
          okText={translate('actions.confirm')}
          onAccept={handleSetLinks()}
          onCancel={handleCloseLinkAvailableModal}
          onSetLinkAvailability={() =>
            setLinkAvailabilityAction(LINK_AVAILABILITY_ACTIONS.AVAILABLE)
          }
        >
          {translate('tasks.links_management.modal.linkAvailableModalLabel')}
        </LinksManagementAvailabilityModal>
      )}
    </>
  );
};

const mapStateToProps = state => ({
  areContentLinksProcessed: getAreContentLinksProcessed(state),
  areDetectionLinksProcessed: getAreDetectionLinksProcessed(state),
  contentLinks: getLinksManagementLinksProcessingContentLinks(state),
  contentLinkUrls: getLinksManagementLinksContentLinkUrlsIntroduced(state),
  detectionLinks: getLinksManagementLinksProcessingDetectionLinks(state),
  detectionLinkUrls: getLinksManagementLinksDetectionLinkUrlsIntroduced(state),
  processingStatus: getLinksManagementLinksProcessingStatus(state),
  selectedReason: getLinksManagementEnforcementSelectedReason(state),
});

export default connect(mapStateToProps, {
  linksManagementLinkAvailabilityUrlFormatError,
  linksManagementLinksSetContentLinksUrls,
  linksManagementLinksSetDetectionLinksUrls,
  linksManagementLinksSetProcessingContentLinks,
  linksManagementLinksSetProcessingDetectionLinks,
  linksManagementSetDeindexationStatusByUrl,
  linksManagementSetLinkAvailability,
  linksManagementSetStatusLinkByUrl,
  linksManagementUrlFormatError,
})(LinksManagementViewerActions);
